import React, { useEffect, useRef, useState } from 'react';
import { api, errText } from '@/lib/api';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useApp } from '@/context/AppContext';
import { useAiStatus } from '@/lib/serverMeta';
import { toast } from 'sonner';
import { Printer, Building2, Upload, Trash2, Loader2, FileCheck, AlertTriangle } from 'lucide-react';
import { PatientIntakeQR } from '@/components/PatientIntakeQR';
import { LEGAL_ENTITY } from '@/pages/legal/LegalLayout';

/** Resize an uploaded logo client-side (max 400px edge, PNG) so it stores
 *  as a compact data URL and renders crisply on pages + PDF letterheads. */
async function logoToDataUrl(file) {
  const bitmap = await createImageBitmap(file);
  const scale = Math.min(1, 400 / Math.max(bitmap.width, bitmap.height));
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(bitmap.width * scale);
  canvas.height = Math.round(bitmap.height * scale);
  canvas.getContext('2d').drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  if (bitmap.close) bitmap.close();
  return canvas.toDataURL('image/png');
}

/** Controlled: the page owns the form so ONE Save button at the bottom saves
 *  identity and settings together (§2 admin note, 2026-09-06 — three
 *  independent Save actions on one page, and the bottom one silently did not
 *  save the logo or name). */
function PracticeIdentityCard({ form, setForm, settings, setSettings }) {
  const logoInputRef = useRef(null);

  const pickLogo = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    try {
      const dataUrl = await logoToDataUrl(f);
      if (dataUrl.length > 500_000) { toast.error('Logo is too large — try a simpler image'); return; }
      setForm((s) => ({ ...s, logo: dataUrl }));
    } catch {
      toast.error('Could not read that image — try a PNG or JPG');
    } finally {
      if (logoInputRef.current) logoInputRef.current.value = '';
    }
  };

  return (
    <Card className="mb-4" data-testid="admin-identity-card">
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2"><Building2 className="h-4 w-4 text-primary" /> Practice identity</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Shown to patients on the intake confirmation and form header, and printed as the letterhead on every signed document.
        </p>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="identity-name">Business / practice name</Label>
            <Input id="identity-name" value={form.name} onChange={(e) => setForm((s) => ({ ...s, name: e.target.value }))} data-testid="admin-identity-name" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="identity-number">Practice number (NPI / registration)</Label>
            <Input id="identity-number" value={form.practice_number} onChange={(e) => setForm((s) => ({ ...s, practice_number: e.target.value }))} placeholder="NPI 1234567890" data-testid="admin-identity-number" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="identity-ein">Tax ID (EIN)</Label>
            <Input id="identity-ein" value={settings.ein || ''} onChange={(e) => setSettings((s) => ({ ...s, ein: e.target.value }))} placeholder="12-3456789" data-testid="admin-identity-ein" />
            <p className="text-xs text-muted-foreground">Filled in wherever an employer's form asks for the provider's EIN.</p>
          </div>
        </div>
        <div className="space-y-1.5">
          <Label>Logo</Label>
          <div className="flex items-center gap-3 flex-wrap">
            <div className="h-16 w-40 rounded-lg border bg-white grid place-items-center overflow-hidden">
              {form.logo
                ? <img src={form.logo} alt="Practice logo preview" className="max-h-14 max-w-[9.5rem] object-contain" data-testid="admin-identity-logo-preview" />
                : <span className="text-xs text-muted-foreground">No logo yet</span>}
            </div>
            <input ref={logoInputRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={pickLogo} data-testid="admin-identity-logo-input" />
            <Button type="button" variant="outline" size="sm" onClick={() => logoInputRef.current?.click()} data-testid="admin-identity-logo-upload">
              <Upload className="h-4 w-4 mr-1" /> {form.logo ? 'Replace' : 'Upload logo'}
            </Button>
            {form.logo && (
              <Button type="button" variant="ghost" size="sm" onClick={() => setForm((s) => ({ ...s, logo: '' }))} data-testid="admin-identity-logo-remove">
                <Trash2 className="h-4 w-4 mr-1" /> Remove
              </Button>
            )}
          </div>
          <p className="text-[11px] text-muted-foreground">PNG or JPG on a light/transparent background works best. Automatically resized.</p>
        </div>
      </CardContent>
    </Card>
  );
}

/** The practice's executed-agreement record (BAA §13).
 *
 *  §13 promises a downloadable executed copy showing the version in force and
 *  how it was executed. It prints the RECORD, not the legal text — the text
 *  lives at /terms and /baa and is versioned there, and duplicating it into a
 *  second renderer would guarantee the two drift apart. Print-to-PDF from the
 *  browser is the download.
 */
function LegalAgreementsCard({ practice }) {
  const acceptance = practice?.legal_acceptance || {};
  const entries = Object.entries(acceptance);
  if (!entries.length) return null;

  const printRecord = () => {
    const rows = entries.map(([, a]) => `
      <tr>
        <td>${a.title || ''}</td>
        <td>${a.version || ''}</td>
        <td>${a.route === 'paper' ? 'Countersigned paper copy' : 'Accepted electronically'}</td>
        <td>${a.accepted_at ? new Date(a.accepted_at).toLocaleString() : ''}</td>
        <td>${a.signed_by || a.accepted_by?.name || ''}${a.accepted_by?.email ? `<br><small>${a.accepted_by.email}</small>` : ''}</td>
      </tr>`).join('');
    const w = window.open('', '_blank');
    if (!w) { toast.error('Allow pop-ups to print this record.'); return; }
    w.document.write(`
      <html><head><title>Executed agreements — ${practice?.name || 'Practice'}</title>
      <style>
        body { font-family: -apple-system, Segoe UI, sans-serif; margin: 40px; color: #111; }
        h1 { font-size: 18px; margin-bottom: 2px; }
        p.sub { color: #555; font-size: 12px; margin-top: 0; }
        table { border-collapse: collapse; width: 100%; margin-top: 20px; font-size: 12px; }
        th, td { border: 1px solid #ccc; padding: 8px; text-align: left; vertical-align: top; }
        th { background: #f4f4f5; }
        footer { margin-top: 24px; font-size: 11px; color: #555; }
      </style></head>
      <body>
        <h1>Executed agreements</h1>
        <p class="sub">${practice?.name || ''} · record generated ${new Date().toLocaleString()}</p>
        <table>
          <thead><tr><th>Agreement</th><th>Version</th><th>How executed</th><th>Effective</th><th>Accepted by</th></tr></thead>
          <tbody>${rows}</tbody>
        </table>
        <footer>
          This is a record of execution, not the agreement itself. The full text of each version is published by
          ${LEGAL_ENTITY}. A countersigned paper copy is available on request and supersedes an electronic acceptance
          while keeping its original effective date.
        </footer>
      </body></html>`);
    w.document.close();
    w.focus();
    w.print();
  };

  return (
    <Card className="mt-4" data-testid="admin-legal-agreements-card">
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2"><FileCheck className="h-4 w-4 text-primary" /> Executed agreements</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {entries.map(([key, a]) => (
          <div key={key} className="flex flex-wrap items-baseline justify-between gap-2 rounded-lg border p-3 text-sm" data-testid={`admin-legal-${key}`}>
            <div>
              <div className="font-medium">{a.title || key}</div>
              <div className="text-xs text-muted-foreground">
                Version {a.version} · {a.route === 'paper' ? 'countersigned paper copy' : 'accepted electronically'}
                {a.superseded_route ? ' (supersedes an earlier electronic acceptance)' : ''}
              </div>
            </div>
            <div className="text-right text-xs text-muted-foreground">
              <div>{a.accepted_at ? new Date(a.accepted_at).toLocaleString() : ''}</div>
              <div>{a.signed_by || a.accepted_by?.name}</div>
            </div>
          </div>
        ))}
        <div className="flex flex-wrap items-center gap-3">
          <Button size="sm" variant="outline" onClick={printRecord} data-testid="admin-legal-print">
            <Printer className="h-3.5 w-3.5 mr-1" /> Print / save as PDF
          </Button>
          <span className="text-xs text-muted-foreground">
            Need a countersigned paper copy for your compliance file? Contact us and we'll execute one.
          </span>
        </div>
      </CardContent>
    </Card>
  );
}

export default function AdminSettings() {
  const { practice, packetTypes, refreshUser } = useApp();
  const ai = useAiStatus();
  const [settings, setSettings] = useState({});
  const [identity, setIdentity] = useState({ name: '', practice_number: '', logo: '' });
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    setSettings(practice?.settings || {});
    if (practice) setIdentity({ name: practice.name || '', practice_number: practice.practice_number || '', logo: practice.logo || '' });
  }, [practice]);

  // One button saves the whole page. Identity first: it is the one that can be
  // refused (a blank name), and a refusal should stop before anything is written.
  const save = async () => {
    if (identity.name.trim().length < 2) { toast.error('Practice name is required'); return; }
    setSaving(true);
    try {
      await api.patch('/practice', { name: identity.name.trim(), practice_number: identity.practice_number.trim(), logo: identity.logo });
      await api.patch('/practice/settings', settings);
      toast.success('Settings saved');
      refreshUser();
    } catch (e) {
      toast.error(errText(e, 'Could not save'));
    } finally {
      setSaving(false);
    }
  };

  // ADR 0001: in Mode 3 the practice bills its own patients, so the patient fee
  // is not ours to quote — the per-document number that means anything to them
  // is our fixed share, which we invoice monthly.
  const practiceCollects = Number(settings.billing_mode ?? 1) === 3;
  // ADR 0004: on Plan C we collect nothing from a patient and charge no
  // per-document fee, so BOTH numbers below are inert. `settings.billing_mode`
  // is still whatever it was before the switch, which is exactly why this reads
  // the organisation's model rather than inferring anything from it.
  const planC = practice?.pricing_model === 'planc';

  return (
    <div className="p-4 lg:p-6 max-w-4xl mx-auto">
      <h1 className="text-base lg:text-lg font-semibold tracking-tight mb-4">Settings</h1>

      <PracticeIdentityCard form={identity} setForm={setIdentity} settings={settings} setSettings={setSettings} />

      {/* ADR 0009: on by default for new practices; a practice created before it reads off. */}
      <Card className="mb-4">
        <CardHeader><CardTitle className="text-base">Workflow</CardTitle></CardHeader>
        <CardContent>
          <div className="flex items-center justify-between gap-4">
            <div>
              <Label className="text-sm font-medium">Send patient submissions straight to the doctor</Label>
              <div className="text-xs text-muted-foreground">
                The AI prepares each case and it goes directly to the doctor's queue — no medical-staff review.
                A case that is missing something (a diagnosis code, an employer-form answer, where to send it)
                still comes to your staff queue first. When the doctor needs more information, the question goes
                to the patient.
              </div>
            </div>
            <Switch checked={!!settings.direct_to_doctor} onCheckedChange={(v) => setSettings(s => ({ ...s, direct_to_doctor: v }))} data-testid="admin-direct-to-doctor-toggle" />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">AI Recommendations</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-sm font-medium">Use real AI (Anthropic Claude)</Label>
              <div className="text-xs text-muted-foreground">Toggle off to use deterministic mock output (great for offline demos).</div>
            </div>
            <Switch checked={settings.ai_mode === 'real'} onCheckedChange={(v) => setSettings(s => ({ ...s, ai_mode: v ? 'real' : 'mock' }))} data-testid="admin-ai-mode-toggle" />
          </div>
          <div className="text-xs text-muted-foreground">Doctors can override per-submission via the “Generate with Real / Mock” buttons in the AI panel.</div>
          {/* §2 admin note (2026-09-06): `ai_mode` defaults to real, and with no
              provider the practice got keyword-match template text and was never told. */}
          {ai.loaded && !ai.configured && (
            <div className="rounded-lg border border-[hsl(45_93%_84%)] bg-[hsl(48_96%_92%)] text-[hsl(32_92%_28%)] px-3 py-2 text-xs flex items-start gap-2" data-testid="admin-ai-not-connected">
              <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
              <span>
                AI is not connected on this deployment yet. Until it is, cases are prepared by keyword matching against your
                catalog with standard template text, and staff see a warning on each one. Contact support to connect it.
              </span>
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="mt-4">
        <CardHeader><CardTitle className="text-base">Pricing</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          {planC ? (
            <div>
              <Label>Patient fee per document</Label>
              <div className="flex items-center gap-3 mt-1">
                <div className="rounded-md border bg-muted px-3 py-2 text-sm font-medium" data-testid="admin-price-display">
                  Yours to set
                </div>
                <span className="text-xs text-muted-foreground">
                  You set what your patients pay, if anything, and you keep every dollar of it — we never take a
                  percentage. We invoice you separately for the software, and our price never varies with what
                  you charge.
                </span>
              </div>
            </div>
          ) : (
          <div>
            <Label>{practiceCollects ? 'Our fee per completed document' : 'Patient fee per document'}</Label>
            <div className="flex items-center gap-3 mt-1">
              <div className="rounded-md border bg-muted px-3 py-2 text-sm font-medium tabular-nums" data-testid="admin-price-display">
                ${Number(practiceCollects ? (settings.platform_share ?? 0) : (settings.launch_price_per_doc ?? 45)).toFixed(2)}
              </div>
              <span className="text-xs text-muted-foreground">
                {practiceCollects
                  ? 'You bill your patients directly. We invoice this per completed document on your monthly bill — contact support to change it.'
                  : 'Set by the platform — contact support if your practice needs a different rate.'}
              </span>
            </div>
          </div>
          )}
          <div><Label>Expected turnaround shown to patients (business days)</Label><Input type="number" min={1} value={settings.expected_turnaround_days ?? 3} onChange={(e) => setSettings(s => ({ ...s, expected_turnaround_days: parseInt(e.target.value || '0') }))} data-testid="admin-turnaround-input" /></div>
          <div><Label>Session timeout (minutes)</Label><Input type="number" value={settings.session_timeout_minutes ?? 30} onChange={(e) => setSettings(s => ({ ...s, session_timeout_minutes: parseInt(e.target.value || '0') }))} /></div>
        </CardContent>
      </Card>

      <LegalAgreementsCard practice={practice} />

      <PatientIntakeQR practice={practice} packetTypes={packetTypes || []} />

      <div className="sticky bottom-0 mt-4 -mx-4 px-4 py-3 bg-background/95 backdrop-blur border-t flex items-center gap-3">
        <Button onClick={save} disabled={saving} data-testid="admin-settings-save">
          {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null} Save settings
        </Button>
        <span className="text-xs text-muted-foreground">Saves everything on this page — identity, logo and settings.</span>
      </div>
    </div>
  );
}
